import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "../../lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-none text-sm font-medium ring-offset-background transition-colors duration-200 ease-in-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground border-2 border-[#161E54] shadow-neo hover:shadow-none hover:translate-x-[4px] hover:translate-y-[4px] active:shadow-none active:translate-x-[4px] active:translate-y-[4px]",
        destructive:
          "bg-destructive text-destructive-foreground border-2 border-[#161E54] shadow-neo hover:shadow-none hover:translate-x-[4px] hover:translate-y-[4px]",
        outline:
          "bg-white text-foreground border-2 border-[#161E54] shadow-neo hover:shadow-none hover:translate-x-[4px] hover:translate-y-[4px] hover:bg-accent",
        secondary:
          "bg-secondary text-secondary-foreground border-2 border-[#161E54] shadow-neo hover:shadow-none hover:translate-x-[4px] hover:translate-y-[4px]",
        ghost: "hover:bg-accent hover:text-accent-foreground border-2 border-transparent hover:border-[#161E54] hover:shadow-neo",
        link: "text-primary underline-offset-4 hover:underline",
        gradient: "bg-primary text-primary-foreground border-2 border-[#161E54] shadow-neo hover:shadow-none hover:translate-x-[4px] hover:translate-y-[4px]",
        success: "bg-emerald-600 text-white border-2 border-[#161E54] shadow-neo hover:shadow-none hover:translate-x-[4px] hover:translate-y-[4px]",
      },
      size: {
        default: "h-11 px-4 py-2",
        sm: "h-9 rounded-none px-3 text-xs",
        lg: "h-12 rounded-none px-8 text-base",
        icon: "h-11 w-11",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
  VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, ...props }, ref) => {
    return (
      <button
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
